import {Inject, Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {TechnicalSecuritySystemModel} from "../_models/technical-security-system-model";
import {ServerEndpoints} from "../../../server.endpoints";
import {KassenSichVState} from "../_models/_enums/kassensichv-state";
import {RetiredTechnicalSecuritySystemModel} from "../_models/retired-technical-security-system-model";
import {LegalTssReinitializeResult} from "../_models/legal-tss-reinitialize-result";

@Injectable({
  providedIn: 'root'
})
export class TechnicalSecuritySystemHttpService {

  constructor(@Inject("BASE_URL") private baseUrl: string, private httpClient: HttpClient) {
  }

  /**
   * (#633) Every method here takes an optional `shardId`. Omitted, it targets the tenant route (`/legal/…`) —
   * `TechnicalSecuritySystemComponent`'s TENANT MODE. Given, it targets the owner-area management route
   * (`/management/legal/{shardId}/…`) instead, PlatformOwner-only, with the shard carried explicitly — the
   * component's OWNER MODE. See {@link SettingsHttpService} for the full reasoning (#613/#633).
   */

  getCurrent(shardId?: string): Observable<TechnicalSecuritySystemModel> {
    const url = shardId
      ? ServerEndpoints.Management_Legal_TSS_GetCurrent.dictReplace({shardId})
      : ServerEndpoints.Legal_TSS_GetCurrent;
    return this.httpClient.get<TechnicalSecuritySystemModel>(`${this.baseUrl}${url}`);
  }

  /** The full retired-TSS history for this shard, newest first — the same rows `GetCurrent().previous` carries. */
  history(shardId?: string): Observable<RetiredTechnicalSecuritySystemModel[]> {
    const url = shardId
      ? ServerEndpoints.Management_Legal_TSS_History.dictReplace({shardId})
      : ServerEndpoints.Legal_TSS_History;
    return this.httpClient.get<RetiredTechnicalSecuritySystemModel[]>(`${this.baseUrl}${url}`);
  }

  create(shardId?: string): Observable<TechnicalSecuritySystemModel> {
    const url = shardId
      ? ServerEndpoints.Management_Legal_TSS_Create.dictReplace({shardId})
      : ServerEndpoints.Legal_TSS_Create;
    return this.httpClient.put<TechnicalSecuritySystemModel>(`${this.baseUrl}${url}`, {});
  }

  changeState(state: KassenSichVState, shardId?: string): Observable<TechnicalSecuritySystemModel> {
    const url = shardId
      ? ServerEndpoints.Management_Legal_TSS_ChangeState.dictReplace({shardId})
      : ServerEndpoints.Legal_TSS_ChangeState;
    return this.httpClient.patch<TechnicalSecuritySystemModel>(`${this.baseUrl}${url}?state=${state}`, {});
  }

  /**
   * Retires the current TSE and mints/initialises a fresh one (#613), PlatformOwner only. Idempotent and
   * resumable — a `RESUME_AT_*` refusal from this same call means calling it again picks up where the last
   * attempt stopped (see `_helper/legal-tss-errors.ts`).
   */
  reInitialize(shardId?: string): Observable<LegalTssReinitializeResult> {
    const url = shardId
      ? ServerEndpoints.Management_Legal_TSS_ReInitialize.dictReplace({shardId})
      : ServerEndpoints.Legal_TSS_ReInitialize;
    return this.httpClient.post<LegalTssReinitializeResult>(`${this.baseUrl}${url}`, {});
  }
}
